import React, { FC, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useLocation, useParams } from 'react-router-dom'
import LoadingSquare from '../../../components/common/LoadingSquare'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useAlert } from 'react-alert'
import { setPath } from '../../../../utils/url'
import { URL } from '../../../constants/url'
import { LangValue } from '../../../../states/types/common'
import MemberListModule from '../../../components/common/MemberListModule'
import SettingModal from '../../../components/common/SettingModal'
import { RequestPatchTaskOnlineMeetingSetting, TaskOnlineMeetingMember } from '../../../../states/types/task'
import { fetchTaskOnlineMeetingSetting, updateTaskOnlineMeetingSetting } from '../../../../states/containers/task/taskSlice'
import { IconName } from '@fortawesome/fontawesome-common-types'
import { RootState } from '../../../../states/containers/store'

const STATUS_LIST = [
  {
    label: "作成する",
    value: true,
  },
  {
    label: "作成しない",
    value: false,
  },
]

const SELECT_LIST = [
  {
    label: "会社全体の設定に準拠する",
    value: "common",
  },
  {
    label: "作成する",
    value: "on",
  },
  {
    label: "作成しない",
    value: "off",
  },
]

const Index: FC = () => {
  const location = useLocation()
  const dispatch = useDispatch()
  const alert = useAlert()
  const { language } = useParams<{ language: LangValue }>()

  const {
    login,
  } = useSelector<RootState, RootState>((state: RootState) => (state))

  const [isCreateOnlineMeetingTask, setIsCreateOnlineMeetingTask] = useState<boolean>(false)

  const [originMemberList, setOriginMemberList] = useState<TaskOnlineMeetingMember[]>([])
  const [commonValue, setCommonValue] = useState<boolean>(true)

  const [isDisplayModal, setIsDisplayModal] = useState<boolean>(false)
  const [targetMembers, setTargetMembers] = useState<TaskOnlineMeetingMember[]>([])

  const [isLoading, setIsLoading] = useState<boolean>(true)

  const openUpdateModal = (memberIds: number[]) => {
    setTargetMembers(originMemberList.filter((v) => memberIds.includes(v.id)))
    setIsDisplayModal(true)
  }

  const closeModal = () => {
    setTargetMembers([])
    setIsDisplayModal(false)
  }

  const buildParam = (type: string) => {
    switch (type) {
      case "on":
        return true
      case "off":
        return false
      default:
        return null
    }
  }

  const buildParamToString = (type: boolean | null) => {
    if (type === null) {
      return "common"
    }
    if (type) return "on"
    return "off"
  }

  const changeCreateOnlineMeetingTask = (data: {commonFlg: boolean, memberIds?: number[], type: string}) => {
    const param: RequestPatchTaskOnlineMeetingSetting = { change_common_setting: data.commonFlg }
    const create_online_meeting = buildParam(data.type)
    if (data.commonFlg) {
      param.create_online_meeting_task = create_online_meeting ?? true
    } else {
      param.member_ids = data.memberIds
      param.use_common_setting = create_online_meeting === null
      if (data.type !== "common") {
        param.create_online_meeting_task = create_online_meeting ?? true
      }
    }
    dispatch(updateTaskOnlineMeetingSetting(
      param,
      () => {
        closeModal()
        if (data.commonFlg) {
          setCommonValue(create_online_meeting ?? true)
          alert.success("会社全体の設定を変更しました")
        } else {
          setOriginMemberList((cur) => cur.map((v) => (
            param.member_ids?.includes(v.id)
              ? { ...v, use_common_setting: param.use_common_setting || false, create_online_meeting_task: param.create_online_meeting_task ?? null }
              : v
          )))
          alert.success("ユーザーの設定を変更しました")
        }
      },
    ))
  }

  useEffect(() => {
    setIsLoading(true)
    dispatch(fetchTaskOnlineMeetingSetting(
      (result) => {
        setOriginMemberList(result.members)
        setCommonValue(result.common.create_online_meeting_task)
        setIsCreateOnlineMeetingTask(result.common.create_online_meeting_task)
        setIsLoading(false)
      },
    ))
  }, [])

  return (
    <section className="task_page setting_page">
      <Helmet>
        <title>
          {
            location.pathname === `/${language}${URL.SETTING_TASK_ONLINE_MEETING_SETTING.path}` ? (
              `Qasee - 設定 - ${login.role_name === "管理者" ? "管理者" : "共通"}設定 - オンラインミーティング時の作業データ設定`
            ) : (
              `Qasee - 工数管理 - オンラインミーティング時の作業データ設定`
            )
          }
        </title>
      </Helmet>
      <section className="breadcrumb">
        {
          location.pathname === `/${language}${URL.SETTING_TASK_ONLINE_MEETING_SETTING.path}` ? (
            <>
              設定
              &nbsp;&gt;&nbsp;
              <Link
                to={setPath(URL.SETTING_COMMON.path, language)}
              >
                {`${login.role_name === "管理者" ? "管理者" : "共通"}設定`}
              </Link>
            </>
          ) : (
            <>
              工数管理
            </>
          )
        }
        &nbsp;&gt;&nbsp;
        オンラインミーティング時の作業データ設定
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          オンラインミーティング時の作業データ設定
        </h1>
      </section>
      <section className="page_description">
        <p style={{ marginBottom: "4px" }}>
          オンラインミーティング時、操作等がない時でも作業データを自動作成するように設定します。
        </p>
      </section>
      {
        isLoading ? (
          <LoadingSquare>
            <>
              設定を読み込んでいます…
            </>
          </LoadingSquare>
        ) : (
          <>
            {
              login.role_target_restriction || (
                <section className="page_contents content-area">
                  <div className="content-block">
                    <div className="block-header" style={{ display: "block" }}>
                      <div className="title" style={{ marginBottom: "16px" }}>
                        会社全体の設定
                      </div>
                    </div>
                    <div className="block-detail">
                      <div
                        className={`graphical_check_box${isCreateOnlineMeetingTask ? " selected" : ""}`}
                        onClick={() => setIsCreateOnlineMeetingTask(!isCreateOnlineMeetingTask)}
                        role="none"
                      >
                        <FontAwesomeIcon
                          icon={['far', `${isCreateOnlineMeetingTask ? 'check-square' : 'square'}` as IconName]}
                          className={`icon ${isCreateOnlineMeetingTask ? "checked" : "no_check"}`}
                        />
                        <div>
                          <div className="check_box_title">
                            オンラインミーティング時の作業データを作成する
                          </div>
                          <div className="check_box_description">
                            オンラインミーティング時、操作等がない時でも作業データを自動作成するように設定します
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          border: "solid 1px #FF0000",
                          borderRadius: "4px",
                          padding: "8px",
                          color: "#FF0000",
                          backgroundColor: "#ffeeee",
                        }}
                      >
                        ※ Windows版限定の機能になります。設定の際はご注意ください。
                      </div>
                      <button
                        type="button"
                        className="mt-4 button"
                        onClick={() => changeCreateOnlineMeetingTask({ commonFlg: true, type: isCreateOnlineMeetingTask ? "on" : "off" })}
                      >
                        設定する
                      </button>
                    </div>
                  </div>
                </section>
              )
            }
            <MemberListModule
              originMemberList={originMemberList.map((v) => ({ ...v, value: v.create_online_meeting_task }))}
              statusList={STATUS_LIST}
              commonValue={commonValue}
              valueLabel="オンラインミーティング時の作業データ設定"
              type="bool"
              modalOpenCallback={(memberIds) => openUpdateModal(memberIds)}
            />
            <SettingModal
              targetMemberList={targetMembers.map((v) => ({ ...v, value: buildParamToString(v.create_online_meeting_task) }))}
              selectList={SELECT_LIST}
              label="オンラインミーティング時の作業データ設定"
              prefix="オンラインミーティング時の作業データを"
              isDisplayModal={isDisplayModal}
              submitCallback={(data: { commonFlg: boolean; memberIds: number[]; type: string }) => changeCreateOnlineMeetingTask(data)}
              closeCallback={closeModal}
              winOnly
            />
          </>
        )
      }
    </section>
  )
}

export default Index
